import { useState, type ReactNode } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { normalizar } from "@/lib/buscadorMenus";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  CONTROL_FILTRO_CLS,
  FILTRO_ACTIVO_CLS,
  FILTRO_ACTIVO_TEXTO_CLS,
  atributosFiltroActivo,
} from "@/components/ui/filter-field";

/**
 * Selector "Ver como" estándar del header de los portales (diseño del Portal Inmobiliaria).
 *
 * Botón con el control estándar de filtro (como SelectorPeriodo): solo el nombre en una línea,
 * sin avatar, iniciales ni rótulo "Ver como" (2026-10-27); la X para volver a la vista propia
 * va dentro del control, a la derecha.
 * Lista con búsqueda, acotada en alto, con una opción por defecto arriba ("Mi vista", "Super
 * Admin") y grupos debajo. Mientras se
 * impersona el botón se marca en primario y aparece una X para volver a la vista propia.
 *
 * Cada portal conserva su contexto de impersonación y su consulta: aquí solo se pinta.
 * No hay conmutador de modo: impersonar es siempre ver como el usuario, en solo lectura.
 */

export interface OpcionVerComo {
  key: string;
  label: string;
  /** Texto chico a la derecha (rol, correo corto, "Por defecto"). */
  nota?: string;
  /** Texto extra para la búsqueda (correo, alias). */
  buscarPor?: string;
  seleccionada: boolean;
  onSelect: () => void;
  /** Ícono propio (p. ej. logo de banco); sin él la fila va solo con el nombre. */
  icono?: ReactNode;
}

export interface GrupoVerComo {
  titulo?: string;
  opciones: OpcionVerComo[];
}

/**
 * Filtro local de "Ver como": cada palabra escrita debe aparecer en el nombre o el correo (o en lo
 * que la opción agregue en `buscarPor`), sin importar acentos ni mayúsculas. El filtro difuso de
 * cmdk dejaba pasar coincidencias sueltas y no encontraba "jose" en "José".
 */
export function coincideVerComo(valor: string, busqueda: string): number {
  const palabras = normalizar(busqueda).split(" ").filter(Boolean);
  if (palabras.length === 0) return 1;
  const texto = normalizar(valor);
  return palabras.every((p) => texto.includes(p)) ? 1 : 0;
}

/** Dos iniciales de un nombre ("Laura Denise" → "LD"). */
export function inicialesVerComo(nombre: string): string {
  return nombre.trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join("") || "?";
}

export function SelectorVerComo({
  etiqueta, impersonando, opcionPorDefecto, grupos, onLimpiar,
  placeholderBusqueda = "Buscar por nombre o correo...", textoVacio = "Nada coincide.", cargando = false,
  ariaLimpiar = "Volver a mi vista", className, compacto = false,
  busqueda, onBusquedaChange, encabezado, pie, onOpenChange, claseBoton,
}: {
  /** Lo que se está viendo ahora (nombre impersonado o la vista propia). */
  etiqueta: string;
  impersonando: boolean;
  /** Ya no se pinta (2026-10-27: el botón lleva solo "Ver como" y el nombre). Se acepta por compatibilidad. */
  iconoVistaPropia?: ReactNode;
  /** Opción fija arriba de la lista (vista propia / ver todo). */
  opcionPorDefecto?: OpcionVerComo;
  grupos: GrupoVerComo[];
  /** Quita la impersonación (la X junto al botón). */
  onLimpiar?: () => void;
  placeholderBusqueda?: string;
  textoVacio?: string;
  cargando?: boolean;
  ariaLimpiar?: string;
  className?: string;
  /** Header móvil: botón más angosto. */
  compacto?: boolean;
  /**
   * Búsqueda en el servidor: con `onBusquedaChange` el texto es controlado y la lista NO se filtra
   * en el navegador (el portal trae lo que coincide). Sin él, filtra cmdk en local.
   */
  busqueda?: string;
  onBusquedaChange?: (texto: string) => void;
  /** Controles arriba de la lista (p. ej. Proyecto + No. de propiedad en Clientes). */
  encabezado?: ReactNode;
  /** Línea al pie de la lista (p. ej. "Mostrando los primeros 50"). */
  pie?: ReactNode;
  /** Avisa al abrir/cerrar, para cargar la lista solo con el popover abierto. */
  onOpenChange?: (abierto: boolean) => void;
  /** Clases extra del botón (p. ej. sin radio izquierdo cuando lleva un prefijo pegado). */
  claseBoton?: string;
}) {
  const [open, setOpenState] = useState(false);
  const setOpen = (v: boolean) => { setOpenState(v); onOpenChange?.(v); };
  const enServidor = !!onBusquedaChange;
  const elegir = (o: OpcionVerComo) => { o.onSelect(); setOpen(false); };

  const fila = (o: OpcionVerComo) => (
    <CommandItem
      key={o.key}
      value={`${o.label} ${o.buscarPor ?? ""} ${o.key}`}
      onSelect={() => elegir(o)}
      className="py-1.5"
    >
      <Check className={cn("mr-2 size-3.5 shrink-0", o.seleccionada ? "opacity-100" : "opacity-0")} />
      {o.icono}
      <span className="min-w-0 flex-1 truncate text-[13px]">{o.label}</span>
      {o.nota && <span className="ml-2 shrink-0 text-[10.5px] text-muted-foreground">{o.nota}</span>}
    </CommandItem>
  );

  return (
    // En móvil ocupa todo el ancho de su fila (los portales lo ponen en una segunda fila); desde
    // sm, el ancho fijo de los controles del encabezado (260 px, igual que "Buscar menú").
    <div className={cn("relative flex w-full min-w-0 items-center sm:w-auto", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={`Ver como: ${etiqueta}`}
            {...atributosFiltroActivo(impersonando)}
            className={cn(
              // Mismo control que los filtros (SelectorPeriodo): una línea, 36 px, borde estándar y
              // tono primario mientras se impersona.
              "h-9 w-full min-w-0 justify-between gap-2 px-3 text-[13px] font-normal",
              impersonando && onLimpiar && "pr-9",
              compacto ? "max-w-[200px]" : "sm:w-[260px] sm:max-w-[260px]",
              impersonando ? FILTRO_ACTIVO_CLS : CONTROL_FILTRO_CLS,
              claseBoton,
            )}
          >
            {/* Solo el nombre (2026-10-27): el "Ver como" va en aria-label y en el placeholder. */}
            <span className={cn("min-w-0 truncate font-medium", impersonando ? FILTRO_ACTIVO_TEXTO_CLS : "text-foreground")}>
              {etiqueta}
            </span>
            {/* Mientras se impersona, el hueco de la derecha es de la X (encima del botón). */}
            {!(impersonando && onLimpiar) && <ChevronsUpDown className="size-3.5 shrink-0 opacity-40" aria-hidden />}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(88vw,300px)] p-0" align="end">
          {encabezado && <div className="space-y-2 border-b p-2">{encabezado}</div>}
          <Command shouldFilter={!enServidor} filter={coincideVerComo}>
            <CommandInput
              placeholder={placeholderBusqueda}
              className="h-9 text-[13px]"
              {...(enServidor ? { value: busqueda ?? "", onValueChange: onBusquedaChange } : {})}
            />
            <CommandList className="max-h-[320px]">
              <CommandEmpty>{cargando ? "Cargando..." : textoVacio}</CommandEmpty>
              {opcionPorDefecto && <CommandGroup>{fila(opcionPorDefecto)}</CommandGroup>}
              {grupos.filter((g) => g.opciones.length > 0).map((g, i) => (
                <CommandGroup key={g.titulo ?? i} heading={g.titulo ? `${g.titulo} (${g.opciones.length})` : undefined}>
                  {g.opciones.map(fila)}
                </CommandGroup>
              ))}
            </CommandList>
            {pie && <div className="border-t px-3 py-2 text-[11px] text-muted-foreground">{pie}</div>}
          </Command>
        </PopoverContent>
      </Popover>
      {/* La X vive dentro del control (a la derecha), como el limpiar de los filtros. */}
      {impersonando && onLimpiar && (
        <button
          type="button"
          onClick={onLimpiar}
          aria-label={ariaLimpiar}
          className="absolute right-1.5 top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded text-primary-strong/70 transition-colors hover:bg-primary/10 hover:text-primary-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      )}
    </div>
  );
}
